import { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { AlertTriangle } from "lucide-react";
import { checkClash } from "../../../../services/ClassroomManagementService.js";
import { clashNames } from "../../../../utils/clashNames";

/* The batch form's live lab check: another running batch in the picked
   lab on the same days and times. The API refuses that save
   (batch.service.js labClashRefusal), so this says so before Save is
   pressed and tells the form through `onChange` to keep Save off.

   An edit that leaves the lab, days, times and status as they were is
   not checked -- the API lets it through too, so a batch saved with an
   overlap before this rule can still have its roster or links edited. */
const scheduleKey = (v) =>
  JSON.stringify([
    String(v?.classroom || ""),
    (v?.days || []).map((d) => d?.value ?? d).sort(),
    v?.startTime ? new Date(v.startTime).getTime() : null,
    v?.endTime ? new Date(v.endTime).getTime() : null,
    v?.batchStatus || "Ongoing",
  ]);

const LabClashNote = ({ batchId, isEditMode, onChange }) => {
  const { values, initialValues } = useFormikContext();
  const [clashes, setClashes] = useState([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const key = scheduleKey(values);
  const unchanged = isEditMode && key === scheduleKey(initialValues);

  useEffect(() => {
    const [classroom, days, start, end, status] = JSON.parse(key);
    const report = (list) => {
      setClashes(list);
      onChangeRef.current?.(list.length > 0);
    };
    if (unchanged || !classroom || !days.length || !start || !end || end <= start || status !== "Ongoing") {
      report([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      checkClash({
        classroom,
        days: days.join(","),
        startTime: new Date(start).toISOString(),
        endTime: new Date(end).toISOString(),
        excludeBatchId: batchId || undefined,
      })
        .then((list) => { if (!cancelled) report((list || []).filter((c) => c.lab)); })
        .catch(() => { if (!cancelled) report([]); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key, unchanged, batchId]);

  // Gone from the form, it no longer holds the save back.
  useEffect(() => () => onChangeRef.current?.(false), []);

  if (!clashes.length) return null;
  return (
    <p className="pg-notice pg-notice--bad">
      <AlertTriangle size={14} aria-hidden="true" />
      {clashes[0].classroomNo || "This classroom"} is already booked at this time by{" "}
      {clashNames(clashes)}. Pick another classroom, days or time to save.
    </p>
  );
};

export default LabClashNote;
